import { isValidEmail } from '@/utils/isValidEmail';
import useDebounce from '@/utils/useDebounce';
import { errToString } from '@typedream/data';
import { Button, Text } from '@typedream/ui';
import classNames from 'classnames';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { useSpaceIDFromPath } from '../../data/space/hooks';
import { useGetProductCollectionQuery, useUpdateContactEmailMutation } from '../../store';
import sharedStyles from '../../styles/Shared.module.css';

export function ProductsContactEmail() {
  const spaceID = useSpaceIDFromPath();
  const { data: productCollection } = useGetProductCollectionQuery([spaceID]);
  const { mutate: updateContactEmail, isLoading, error } = useUpdateContactEmailMutation();

  const [email, setemail] = useState(productCollection?.field?.contact_email);
  const [emailError, setemailError] = useState(undefined);

  const onUpdateEmail = async () => {
    if (!email) {
      setemailError("Email can't be empty");
      return;
    }
    if (!isValidEmail(email)) {
      setemailError('Invalid email address');
      return;
    }
    try {
      await updateContactEmail({ spaceID, contactEmail: email.toLowerCase() }).unwrap();
    } catch (e) {
      toast.error(errToString(e));
    }
  };

  const resetEmail = useCallback(() => {
    setemail(productCollection?.field?.contact_email);
  }, [productCollection?.field?.contact_email]);

  useEffect(() => {
    resetEmail();
  }, [productCollection?.field?.contact_email, resetEmail]);

  const debouncedEmail = useDebounce(email, 1000);

  useEffect(() => {
    (function validate() {
      if (!debouncedEmail) {
        setemailError("Email can't be empty");
        return;
      }
      if (!isValidEmail(debouncedEmail)) {
        setemailError('Invalid email address');
        return;
      }
      setemailError(undefined);
    })();
  }, [debouncedEmail]);

  return (
    <form
      onSubmit={(e) => {
        e.preventDefault();
        onUpdateEmail();
      }}
      className="flex flex-col items-start gap-4"
    >
      <div className="flex flex-col gap-2.5">
        <Text as="h2" size="md" className="tracking-[-0.01em]">
          Contact Email
        </Text>
        <Text as="p" size="sm" weight="medium" className="text-secondary">
          This will be the email displayed on purchase receipts to allow customers to contact you
          about their purchase.
        </Text>
      </div>
      <input
        className={classNames(sharedStyles.inputMedium, {
          [sharedStyles.inputError]: !!error || !!emailError,
        })}
        type="text"
        value={email}
        placeholder="e.g. someone@email.com"
        onChange={(e) => {
          setemail(e.target.value.trim());
        }}
      />
      {emailError && (
        <Text size="xs" className="text-danger">
          {emailError}
        </Text>
      )}
      <div className="flex items-center gap-2">
        <Button
          type="submit"
          isLoading={isLoading}
          isDisabled={email && email === productCollection?.field?.contact_email}
        >
          Update Email
        </Button>
        {email && email !== productCollection?.field?.contact_email && (
          <Button variant="ghost" onClick={resetEmail}>
            Cancel
          </Button>
        )}
      </div>
    </form>
  );
}
